import React, { useState, useEffect } from "react";
import { Container, Form, Row, Col } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import Select from 'react-select';
import "./FormStyles.css";
import "./UnifiedFormStyles.css";
import AppButton from "../../Components/MerchantButton";
import { getRegionConfig } from "./config/regionConfig";

function RegionForm({ navigation, primaryDetails, setPrimaryDetails, selectedRegion }) {
  const [isError, setIsError] = useState({
    region: false,
  });
  const [regionConfig, setRegionConfig] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [isCustomMunicipality, setIsCustomMunicipality] = useState(false);
  
  // Region options
  const REGION_OPTIONS = [
  "NCR 1",
  "NCR 2",
  "NCR 3",
  "North Luzon",
  "Central Luzon",
  "South Luzon",
  "Visayas",
  "Mindanao",
  "Siargao"
  ];

  // Set default region to "North Luzon" on component mount
  useEffect(() => {
    if (!primaryDetails.region) {
      const defaultRegion = "North Luzon";
      setPrimaryDetails(prev => ({ ...prev,
        region: defaultRegion,
      }));
      
      // Load config for default region
      const config = getRegionConfig(defaultRegion);
      if (config) {
        setRegionConfig(config);
        const initialErrors = {};
        Object.keys(config.formFields).forEach(field => {
          if (field !== 'description' && field !== 'mall_name') {
            initialErrors[config.formFields[field]] = false;
          }
        });
        setIsError({ region: false, ...initialErrors });
      }
    }
  }, []);

  const isFormValid = () => {
    if (!primaryDetails.region) return false;
    if (!regionConfig) return false;

    const { formFields } = regionConfig;
    return !!primaryDetails[formFields.province] &&
      !!primaryDetails[formFields.municipality] &&
      !!primaryDetails[formFields.mall_type];
  };
  useEffect(() => {
    if (selectedRegion) {
      const config = getRegionConfig(selectedRegion);
      if (config) {
        setRegionConfig(config);
      
        const initialErrors = {};
        Object.keys(config.formFields).forEach(field => {
          if (field !== 'description' && field !== 'mall_name') {
            initialErrors[config.formFields[field]] = false;
          }
        });
        setIsError(initialErrors);
      }
    }
  }, [selectedRegion]);

  // Sync selectedProvince when primaryDetails changes (for auto-fill)
  useEffect(() => {
    if (regionConfig && primaryDetails[regionConfig.formFields.province]) {
      const provinceValue = primaryDetails[regionConfig.formFields.province];
      setSelectedProvince({
        value: provinceValue,
        label: provinceValue
      });
    }
    
    // Sync selectedMunicipality when primaryDetails changes (for auto-fill)
    if (regionConfig && primaryDetails[regionConfig.formFields.municipality]) {
      const municipalityValue = primaryDetails[regionConfig.formFields.municipality];
      setSelectedMunicipality({
        value: municipalityValue,
        label: municipalityValue
      });
    }
  }, [primaryDetails, regionConfig]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    
    // Handle React Select change for province
    if (e && !e.target && e.value && name === formFields.province) {
      const newValue = e.value;
      setPrimaryDetails(prev => ({ ...prev,
        [name]: newValue,
        [formFields.municipality]: ''
      }));
      setSelectedProvince({ value: newValue, label: newValue });
      setSelectedMunicipality(null);
    }
    // Handle React Select change for municipality
    else if (e && !e.target && e.value && name === formFields.municipality) {
      const newValue = e.value;
      setPrimaryDetails(prev => ({ ...prev,
        [name]: newValue,
      }));
      setSelectedMunicipality({ value: newValue, label: newValue });
    } 
    else if (name === formFields?.mall_type) {
      setPrimaryDetails(prev => ({ ...prev,
        [name]: value,
        [formFields.mall_name]: '',
        mall_name: '',
        other_mall_name: '',
        floor_no: ''
      }));
    }
    // Handle regular input change
    else if (name && value !== undefined) {
      setPrimaryDetails(prev => ({ ...prev,
        [name]: value,
      }));
    }

    // Clear error when user makes a selection
    if (name && isError[name]) {
      setIsError({
        ...isError,
        [name]: false,
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    // Validate region selection
    if (!primaryDetails.region) {
      errors.region = true;
    }

    // If region is selected, validate other fields
    if (regionConfig) {
      const { formFields } = regionConfig;

      if (!primaryDetails[formFields.province]) {
        errors[formFields.province] = true;
      }
      if (!primaryDetails[formFields.municipality ]) {
        errors[formFields.municipality ] = true;
      }
      if (!primaryDetails[formFields.mall_type]) {
        errors[formFields.mall_type] = true;
      }
    }

    setIsError(errors);

    const hasErrors = Object.values(errors).some(error => error);
    if (hasErrors) {
      toast.error("Please fill in all required fields", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const mallTypeField = regionConfig.formFields.mall_type;
      const mallType = primaryDetails[mallTypeField];

      if (mallType === "Standalone (single, standalone merchant)" || mallType === "Non-Mall (located in a compound/grouped with other merchants)") {
        navigation.go("merchant-location");
      } else {
        navigation.go("mall-selector");
      }
    }
  };

  const handleBack = () => {
   navigation.previous();
  };

  // Show loading if region is selected but config is not loaded yet
  if (primaryDetails.region && !regionConfig) {
    return (
      <div className="form-bg form-loading">
        <div>Loading region configuration...</div>
      </div>
    );
  }

  const { formFields, provinces, municipalities, title, description } = regionConfig || {};

  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">
          <Col md={10}>
            <div className="form-main-header">
              <h4 className="form-title">
                {primaryDetails.region ? title : "SELECT REGION"}
              </h4>
              <p className="form-desc">
                {primaryDetails.region ? description : "Please select your region to continue"}
              </p>
            </div>
            <div className="form-main">
              <div className="form-main-content">
                <Form className="form-form">
                  {/* Region Selection - Always visible */}
                  <Form.Group className="mb-5" controlId="region">
                    <Form.Label className="form-label">
                      Region <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Select
                      name="region"
                      value={primaryDetails.region || ""}
                      onChange={(e) => {
                        setSelectedProvince(null); // Reset province selection
                        setIsCustomMunicipality(false); // Reset custom input state
                        handleInputChange(e);
                      }}
                      className={`form-input${
                        isError.region ? " form-input-error" : ""
                      }`}
                    >
                      <option></option>
                      {REGION_OPTIONS.map((region, index) => (
                        <option key={`region-${index}`} value={region}>
                          {region}
                        </option>
                      ))}
                    </Form.Select>
                    {isError.region && (
                      <div className="form-error">Please select a region</div>
                    )}
                  </Form.Group>

                  {/* Show province/municipality only when region is selected */}
                  {primaryDetails.region && regionConfig && (
                  <>
                  <Form.Group className="mb-5" controlId={formFields.province}>
                    <Form.Label className="form-label">
                      Province <span className="form-required">*</span>
                    </Form.Label>
                    <Select
                      name={formFields.province}
                      value={selectedProvince}
                      onChange={(selectedOption) => {
                        setSelectedProvince(selectedOption);
                        setIsCustomMunicipality(false); // Reset custom input state
                        handleInputChange({
                          target: {
                            name: formFields.province,
                            value: selectedOption ? selectedOption.value : ''
                          }
                        });
                      }}
                      options={provinces.map(province => ({
                        value: province,
                        label: province
                      }))}
                      className={`react-select-container${
                        isError[formFields.province] ? " form-input-error" : ""
                      }`}
                      classNamePrefix="react-select"
                      placeholder="Select a province..."
                      isSearchable={true}
                      noOptionsMessage={() => "No matching provinces found"}
                      menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                    {isError[formFields.province] && (
                      <div className="form-error">
                        Please select a province
                      </div>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-5" controlId={formFields.municipality}>
                    <Form.Label className="form-label">
                      Municipality/City <span className="form-required">*</span>
                    </Form.Label>
                    
                    {/* Show text input when OTHERS is selected, otherwise show dropdown */}
                    {isCustomMunicipality ? (
                      <Form.Control
                        type="text"
                        name={formFields.municipality}
                        value={primaryDetails[formFields.municipality] || ""}
                        onChange={handleInputChange}
                        placeholder="Enter Municipality/City name..."
                        className={`form-input${isError[formFields.municipality] ? " form-input-error" : ""}`}
                      />
                    ) : (
                      <Select
                        name={formFields.municipality}
                        value={primaryDetails[formFields.municipality] ? 
                          { value: primaryDetails[formFields.municipality], label: primaryDetails[formFields.municipality] } : 
                          null
                        }
                        onChange={(selectedOption) => {
                          if (selectedOption?.value === "OTHERS") {
                            setIsCustomMunicipality(true);
                            setPrimaryDetails(prev => ({ ...prev,
                              [formFields.municipality]: ""
                            }));
                          } else {
                            setIsCustomMunicipality(false);
                            handleInputChange({
                              target: {
                                name: formFields.municipality,
                                value: selectedOption ? selectedOption.value : ''
                              }
                            });
                          }
                        }}
                        options={
                          Array.isArray(municipalities) 
                            ? municipalities.map(municipality => ({
                                value: municipality,
                                label: municipality
                              }))
                            : selectedProvince && municipalities[selectedProvince.value] 
                            ? municipalities[selectedProvince.value].map(municipality => ({
                                value: municipality,
                                label: municipality
                              }))
                            : []
                        }
                        className={`react-select-container${
                          isError[formFields.municipality] ? " form-input-error" : ""
                        }`}
                        classNamePrefix="react-select"
                        placeholder="Select a municipality..."
                        isSearchable={true}
                        noOptionsMessage={() => "No matching municipalities found"}
                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    )}
                    
                    {isError[formFields.municipality] && (
                      <div className="form-error">
                        Please select a municipality
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-5" controlId={formFields.mall_type}>
                    <Form.Label className="form-label">
                      Mall Type <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Select
                      name={formFields.mall_type}
                      value={primaryDetails[formFields.mall_type] || ""}
                      onChange={handleInputChange}
                      className={`form-input${
                        isError[formFields.mall_type] ? " form-input-error" : ""
                      }`}
                    >
                      <option></option>
                      {["Mall (single or multiple buildings)", "Non-Mall (located in a compound/grouped with other merchants)", "Standalone (single, standalone merchant)"].map((type, index) => (
                        <option key={`mall-type-${index}`} value={type}>
                          {type}
                        </option>
                      ))}
                    </Form.Select>
                    {isError[formFields.mall_type] && (
                      <div className="form-error">
                        Please select a mall type
                      </div>
                    )}
                  </Form.Group>
                  <div>
                    <p className="form-note-text">
                      <em>Note: Choose Mall if your business is located inside a shopping mall, or Non-Mall if it's a standalone location</em>
                    </p>
                  </div>
                  </>
                  )}

                  <div className="form-nav-btns">
                    <AppButton
                      onClick={handleBack}
                      variant="back"
                      className="form-btn variant-secondary"
                    >
                      ← Back
                    </AppButton>
                    <AppButton
                      onClick={() => {
                        if (!primaryDetails.region) {
                          setIsError({ ...isError, region: true });
                          toast.error("Please select a region");
                        } else {
                          handleSubmit();
                        }
                      }}
                      variant="next"
                      className="form-btn variant-primary"
                      disabled={!isFormValid()}
                    >
                        Next →
                    </AppButton>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default RegionForm;
